import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/solid';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://sistema.notaria4otavalo.com.ec';
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.error || 'Error en la solicitud al servidor.';
    const details = error.response?.data?.detalle ? JSON.stringify(error.response.data.detalle) : '';
    toast.error(`${message} ${details}`, { position: 'top-right', autoClose: 5000 });
    return Promise.reject(error);
  }
);

const DocumentManagement = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedAdditionalFile, setSelectedAdditionalFile] = useState(null);
  const [fechaDocumentoMain, setFechaDocumentoMain] = useState('');
  const [fechaDocumentoAdditional, setFechaDocumentoAdditional] = useState('');
  const [days, setDays] = useState([]);
  const [isLoadingMain, setIsLoadingMain] = useState(false);
  const [isLoadingAdditional, setIsLoadingAdditional] = useState(false);
  const [isDeletingDay, setIsDeletingDay] = useState(null); // Para manejar el estado de eliminación por día
  const [isDeletingAdditionalFile, setIsDeletingAdditionalFile] = useState(null); // Para manejar el estado de eliminación por archivo adicional
  const [errorMain, setErrorMain] = useState(null);
  const [errorAdditional, setErrorAdditional] = useState(null);

  // Estados para paginación y búsqueda de días
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
const [searchDate, setSearchDate] = useState('');

  // Estados para el listado de archivos adicionales
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [additionalFilesPage, setAdditionalFilesPage] = useState(1);
  const [additionalFilesTotalPages, setAdditionalFilesTotalPages] = useState(1);
const [additionalFilesSearchDate, setAdditionalFilesSearchDate] = useState('');

  // Verificación de permisos
  useEffect(() => {
    if (!loading && (!user || !user.is_notario)) {
      toast.error('No tienes permisos para acceder a esta página.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Fetch días y archivos adicionales
  const fetchDays = useCallback(async () => {
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
      };
      if (searchDate) {
        params.search_date = searchDate;
      }
      const response = await api.get('/api/audit/document-days/', { params });
      setDays(response.data.days || []);
setTotalPages(response.data.total_pages || 1);
      setErrorMain(null);
    } catch (err) {
      if (err.response?.status === 401) return;
      setErrorMain('Error al cargar los días con documentos.');
    }
  }, [currentPage, pageSize, searchDate]);

  const fetchAdditionalFiles = useCallback(async () => {
    try {
      const params = {
        page: additionalFilesPage,
        page_size: pageSize,
      };
      if (additionalFilesSearchDate) {
        params.search_date = additionalFilesSearchDate;
      }
      const response = await api.get('/api/audit/additional-files/', { params });
      setAdditionalFiles(response.data.files || []);
setAdditionalFilesTotalPages(response.data.total_pages || 1);
      setErrorAdditional(null);
    } catch (err) {
      if (err.response?.status === 401) return;
      setErrorAdditional('Error al cargar los archivos adicionales.');
    }
  }, [additionalFilesPage, pageSize, additionalFilesSearchDate]);

  useEffect(() => {
    if (!loading && user && user.is_notario) {
      fetchDays();
      fetchAdditionalFiles();
    }
  }, [user, loading, fetchDays, fetchAdditionalFiles]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && !['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(file.type)) {
      toast.error('Por favor, selecciona un archivo Excel (.xls, .xlsx).', { position: 'top-right' });
      return;
    }
    setSelectedFile(file);
  };

  const handleAdditionalFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type !== 'text/csv') {
      toast.error('Por favor, selecciona un archivo CSV (.csv).', { position: 'top-right' });
      return;
    }
    setSelectedAdditionalFile(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile || !fechaDocumentoMain) {
      toast.error('Por favor selecciona un archivo principal y una fecha.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('fecha_documento', fechaDocumentoMain);

    setIsLoadingMain(true);
    setErrorMain(null);

    try {
      await api.post('/api/audit/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Archivo principal cargado exitosamente.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setSelectedFile(null);
      setFechaDocumentoMain('');
      await fetchDays();
      await fetchAdditionalFiles();
    } catch (error) {
      setErrorMain('Error al cargar el archivo principal.');
    } finally {
      setIsLoadingMain(false);
    }
  };

  const onSubmitAdditional = async (e) => {
    e.preventDefault();
    if (!selectedAdditionalFile || !fechaDocumentoAdditional) {
      toast.error('Por favor selecciona un archivo adicional y una fecha.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    const formData = new FormData();
    formData.append('additional_file', selectedAdditionalFile);
    formData.append('fecha_documento', fechaDocumentoAdditional);

    setIsLoadingAdditional(true);
    setErrorAdditional(null);

    try {
      await api.post('/api/audit/upload-additional/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Archivo adicional cargado exitosamente.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setSelectedAdditionalFile(null);
      setFechaDocumentoAdditional('');
      await fetchDays();
      await fetchAdditionalFiles();
    } catch (error) {
      setErrorAdditional('Error al cargar el archivo adicional.');
    } finally {
      setIsLoadingAdditional(false);
    }
  };

  const handleDelete = async (day) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar los documentos del día ${day}? Esta acción no se puede deshacer.`)) {
      return;
    }

    setIsDeletingDay(day);
    try {
      await api.delete('/api/audit/delete-document/', {
        data: { fecha_documento: day },
      });
      toast.success(`Documentos del día ${day} eliminados.`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      await fetchDays();
      await fetchAdditionalFiles();
    } catch (err) {
      setErrorMain('Error al eliminar documentos.');
    } finally {
      setIsDeletingDay(null);
    }
  };

  const handleDeleteAdditionalFile = async (fileId) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar este archivo adicional? Esta acción no se puede deshacer.`)) {
      return;
    }

    setIsDeletingAdditionalFile(fileId);
    try {
      await api.delete('/api/audit/delete-additional-file/', {
        data: { file_id: fileId },
      });
      toast.success('Archivo adicional eliminado exitosamente.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      await fetchAdditionalFiles();
      await fetchDays();
    } catch (err) {
      setErrorAdditional('Error al eliminar el archivo adicional.');
    } finally {
      setIsDeletingAdditionalFile(null);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleAdditionalFilesPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= additionalFilesTotalPages) {
      setAdditionalFilesPage(newPage);
    }
  };

  if (loading) return <div className="text-white text-center">Cargando...</div>;
  if (!user) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white p-6 bg-gradient-to-b from-gray-900 to-gray-800"
    >
      <header className="flex justify-between items-center mb-10">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-16 h-auto mr-4" />
          <h1 className="text-3xl font-bold">Gestión de Documentos</h1>
        </div>
        <Link
          to="/dashboard/notario"
          className="bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all duration-300"
          aria-label="Volver al Dashboard"
        >
          Volver al Dashboard
        </Link>
      </header>
      <main className="flex-1">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-8 rounded-xl shadow-2xl mb-8"
          aria-busy={isLoadingMain || isLoadingAdditional}
        >
          <h2 className="text-2xl font-semibold mb-6 text-gray-200">Cargar Documentos</h2>
          <div className="space-y-8">
            {/* Sección para el archivo principal */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-gray-200">Cargar Documento Principal (Excel)</h3>
              <form onSubmit={onSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="file-main" className="block text-lg font-semibold mb-2 text-gray-200">Seleccionar Archivo Principal (.xls, .xlsx)</label>
                    <input
                      type="file"
                      id="file-main"
                      accept=".xls,.xlsx"
                      onChange={handleFileChange}
                      className="w-full p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label htmlFor="fecha_documento_main" className="block text-lg font-semibold mb-2 text-gray-200">Fecha del Documento</label>
                    <input
                      type="date"
                      id="fecha_documento_main"
                      value={fechaDocumentoMain}
                      onChange={(e) => setFechaDocumentoMain(e.target.value)}
                      className="w-full p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                      aria-required="true"
                      aria-label="Fecha del documento principal"
                    />
                  </div>
                </div>
                {errorMain && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-4 text-red-500"
                  >
                    {errorMain}
                  </motion.div>
                )}
                <motion.button
                  type="submit"
                  className={`mt-6 w-full bg-notaria-yellow text-notaria-blue-1 font-bold py-2 rounded-full shadow-lg hover:bg-notaria-red hover:text-white transition-all duration-300 transform hover:scale-105 ${
                    isLoadingMain ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  whileHover={{ scale: !isLoadingMain ? 1.05 : 1 }}
                  whileTap={{ scale: !isLoadingMain ? 0.95 : 1 }}
                  disabled={isLoadingMain}
                  aria-label="Cargar documento principal"
                  aria-busy={isLoadingMain}
                >
                  {isLoadingMain ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" aria-hidden="true" />
                      Cargando...
                    </>
                  ) : (
                    'Cargar Documento Principal'
                  )}
                </motion.button>
              </form>
            </div>

            {/* Sección para el archivo adicional */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-gray-200">Cargar Ítems Adicionales (CSV)</h3>
              <form onSubmit={onSubmitAdditional}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="additional_file" className="block text-lg font-semibold mb-2 text-gray-200">Seleccionar Archivo Adicional (.csv)</label>
                    <input
                      type="file"
                      id="additional_file"
                      accept=".csv"
                      onChange={handleAdditionalFileChange}
                      className="w-full p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label htmlFor="fecha_documento_additional" className="block text-lg font-semibold mb-2 text-gray-200">Fecha del Documento</label>
                    <input
                      type="date"
                      id="fecha_documento_additional"
                      value={fechaDocumentoAdditional}
                      onChange={(e) => setFechaDocumentoAdditional(e.target.value)}
                      className="w-full p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                      aria-required="true"
                      aria-label="Fecha del documento adicional"
                    />
                  </div>
                </div>
                {errorAdditional && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-4 text-red-500"
                  >
                    {errorAdditional}
                  </motion.div>
                )}
                <motion.button
                  type="submit"
                  className={`mt-6 w-full bg-notaria-yellow text-notaria-blue-1 font-bold py-2 rounded-full shadow-lg hover:bg-notaria-red hover:text-white transition-all duration-300 transform hover:scale-105 ${
                    isLoadingAdditional ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  whileHover={{ scale: !isLoadingAdditional ? 1.05 : 1 }}
                  whileTap={{ scale: !isLoadingAdditional ? 0.95 : 1 }}
                  disabled={isLoadingAdditional}
                  aria-label="Cargar ítems adicionales"
                  aria-busy={isLoadingAdditional}
                >
                  {isLoadingAdditional ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" aria-hidden="true" />
                      Cargando...
                    </>
                  ) : (
                    'Cargar Ítems Adicionales'
                  )}
                </motion.button>
              </form>
            </div>
          </div>
        </motion.div>

        {/* Sección para el listado de archivos adicionales */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-4xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-8 rounded-xl shadow-2xl mb-8"
          aria-busy={isDeletingAdditionalFile !== null}
        >
          <h2 className="text-2xl font-semibold mb-6 text-gray-200">Archivos Adicionales Cargados</h2>
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
            <div className="relative w-full sm:w-64 mb-4 sm:mb-0">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
              <input
                type="date"
                value={additionalFilesSearchDate}
                onChange={(e) => {
                  setAdditionalFilesSearchDate(e.target.value);
                  setAdditionalFilesPage(1);
                }}
                className="w-full pl-10 p-2 rounded-lg bg-notaria-blue-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                aria-label="Buscar archivos adicionales por fecha"
              />
            </div>
          </div>
          {additionalFiles.length === 0 ? (
            <p className="text-center text-lg text-gray-400">No hay archivos adicionales cargados.</p>
          ) : (
            <div className="w-full">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-notaria-blue-1 text-white">
                    <th className="p-3 text-left">Fecha del Documento</th>
                    <th className="p-3 text-left">Archivo</th>
                    <th className="p-3 text-left">Subido por</th>
                    <th className="p-3 text-left">Fecha de Carga</th>
                    <th className="p-3 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {additionalFiles.map((file, index) => (
                    <motion.tr
                      key={file.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="border-b border-notaria-blue-2 hover:bg-notaria-blue-2"
                    >
                      <td className="p-3">{file.fecha_documento}</td>
                      <td className="p-3">{file.archivo.split('/').pop()}</td>
                      <td className="p-3">{file.uploaded_by || 'Desconocido'}</td>
                      <td className="p-3">{new Date(file.created_at).toLocaleString()}</td>
                      <td className="p-3">
                        <motion.button
                          onClick={() => handleDeleteAdditionalFile(file.id)}
                          className={`bg-red-500 text-white font-bold py-1 px-3 rounded-full hover:bg-red-700 transition-all duration-300 flex items-center ${
                            isDeletingAdditionalFile === file.id ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={isDeletingAdditionalFile === file.id}
                          whileHover={{ scale: isDeletingAdditionalFile === file.id ? 1 : 1.05 }}
                          whileTap={{ scale: isDeletingAdditionalFile === file.id ? 1 : 0.95 }}
                          aria-label={`Eliminar archivo adicional ${file.id}`}
                          aria-busy={isDeletingAdditionalFile === file.id}
                        >
                          {isDeletingAdditionalFile === file.id ? (
                            <>
                              <ArrowPathIcon className="w-4 h-4 inline mr-1 animate-spin" aria-hidden="true" />
                              Eliminando...
                            </>
                          ) : (
                            'Eliminar'
                          )}
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
              {additionalFilesTotalPages > 1 && (
                <div className="flex justify-center items-center space-x-4 mt-4">
                  <motion.button
                    onClick={() => handleAdditionalFilesPageChange(additionalFilesPage - 1)}
                    disabled={additionalFilesPage === 1}
                    className="p-2 bg-notaria-blue-2 rounded-full hover:bg-notaria-yellow hover:text-notaria-blue-1 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Página anterior de archivos adicionales"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </motion.button>
                  <span aria-live="polite">
                    Página {additionalFilesPage} de {additionalFilesTotalPages}
                  </span>
                  <motion.button
                    onClick={() => handleAdditionalFilesPageChange(additionalFilesPage + 1)}
                    disabled={additionalFilesPage === additionalFilesTotalPages}
                    className="p-2 bg-notaria-blue-2 rounded-full hover:bg-notaria-yellow hover:text-notaria-blue-1 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Página siguiente de archivos adicionales"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </motion.button>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Sección para el listado de días con documentos */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="max-w-4xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-8 rounded-xl shadow-2xl"
          aria-busy={isDeletingDay !== null}
        >
          <h2 className="text-2xl font-semibold mb-6 text-gray-200">Documentos Cargados</h2>
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
            <div className="relative w-full sm:w-64 mb-4 sm:mb-0">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
              <input
                type="date"
                value={searchDate}
                onChange={(e) => {
                  setSearchDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 p-2 rounded-lg bg-notaria-blue-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                aria-label="Buscar documentos por fecha"
              />
            </div>
          </div>
          {days.length === 0 ? (
            <p className="text-center text-lg text-gray-400">No hay documentos cargados.</p>
          ) : (
            <div className="w-full">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-notaria-blue-1 text-white">
                    <th className="p-3 text-left">Fecha del Documento</th>
                    <th className="p-3 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((day, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="border-b border-notaria-blue-2 hover:bg-notaria-blue-2"
                    >
                      <td className="p-3">{day}</td>
                      <td className="p-3 flex space-x-2">
                        <Link
                          to={`/tramites?fecha=${day}`}
                          className="bg-notaria-yellow text-notaria-blue-1 font-bold py-1 px-3 rounded-full hover:bg-notaria-red hover:text-white transition-all duration-300"
                          aria-label={`Ver trámites del día ${day}`}
                        >
                          Ver
                        </Link>
                        <motion.button
                          onClick={() => handleDelete(day)}
                          className={`bg-red-500 text-white font-bold py-1 px-3 rounded-full hover:bg-red-700 transition-all duration-300 flex items-center ${
                            isDeletingDay === day ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={isDeletingDay === day}
                          whileHover={{ scale: isDeletingDay === day ? 1 : 1.05 }}
                          whileTap={{ scale: isDeletingDay === day ? 1 : 0.95 }}
                          aria-label={`Eliminar documentos del día ${day}`}
                          aria-busy={isDeletingDay === day}
                        >
                          {isDeletingDay === day ? (
                            <>
                              <ArrowPathIcon className="w-4 h-4 inline mr-1 animate-spin" aria-hidden="true" />
                              Eliminando...
                            </>
                          ) : (
                            'Eliminar'
                          )}
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-4 mt-4">
                  <motion.button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 bg-notaria-blue-2 rounded-full hover:bg-notaria-yellow hover:text-notaria-blue-1 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Página anterior"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </motion.button>
                  <span aria-live="polite">
                    Página {currentPage} de {totalPages}
                  </span>
                  <motion.button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 bg-notaria-blue-2 rounded-full hover:bg-notaria-yellow hover:text-notaria-blue-1 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Página siguiente"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </motion.button>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </main>
      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default DocumentManagement;